import React, {
  type ReactNode,
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

// ** Configs
import themeConfig from '../config';

// ** Hooks
import useSystemColorScheme from '../hooks/useSystemColorScheme';

// ** Types
import type { Direction, PaletteMode } from '@mui/material';
import type {
  AppBar,
  ContentWidth,
  Footer,
  Mode,
  Skin,
  ThemeColor,
  VerticalNavToggle,
} from '../types';
import clsx from 'clsx';
import { exception } from '@erikdakoda/log/exception';
import { useStorageUserId } from '@erikdakoda/ui/hooks/useStorageUserId';
import { readUserScopedItem, userScopedStorage } from '@erikdakoda/ui/shared/userScopedStorage';

export type Settings = {
  mode: PaletteMode;
  preferredMode: Mode;
  skin: Skin;
  themeColor: ThemeColor;
  lightColor: string;
  darkColor: string;
  primaryColor: string;
  secondaryColor: string;
  backColor: string;
  backTheme?: string;
  appBar?: AppBar;
  backgroundFilter?: string;
  footer?: Footer;
  navHidden?: boolean; // navigation menu
  navBelow?: boolean; // move horizontal navigation below app bar
  direction: Direction;
  navCollapsed: boolean;
  navVisible: boolean;
  menuTextHidden: boolean;
  contentWidth: ContentWidth;
  contentPadding: number;
  layout?: 'vertical' | 'horizontal';
  lastLayout?: 'vertical' | 'horizontal';
  verticalNavToggleType: VerticalNavToggle;
  toastPosition?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
};

export type PageSpecificSettings = Partial<Settings>;
export type SettingsUpdater = Settings | ((current: Settings) => Settings);
export type SettingsContextValue = {
  settings: Settings;
  saveSettings: (updatedSettings: SettingsUpdater) => void;
};

interface SettingsProviderProps {
  children: ReactNode;
  pageSettings?: PageSpecificSettings | void;
  className?: string;
}

const getInitialSettings: () => Settings = () => ({
  mode: themeConfig.config.mode === 'dark' ? 'dark' : 'light',
  preferredMode: themeConfig.config.mode,
  skin: themeConfig.config.skin,
  themeColor: 'primary',
  lightColor: themeConfig.config.lightColor,
  darkColor: themeConfig.config.darkColor,
  primaryColor: themeConfig.config.primaryColor,
  secondaryColor: themeConfig.config.secondaryColor,
  backColor: themeConfig.config.backColor,
  backTheme: themeConfig.config.backTheme,
  footer: themeConfig.config.footer,
  layout: themeConfig.config.layout,
  lastLayout: themeConfig.config.layout,
  direction: themeConfig.config.direction,
  navHidden: themeConfig.config.navHidden,
  navBelow: themeConfig.config.navBelow,
  backgroundFilter: themeConfig.config.backgroundFilter,
  navCollapsed: themeConfig.config.navCollapsed,
  navVisible: false,
  menuTextHidden: themeConfig.config.menuTextHidden,
  contentWidth: themeConfig.config.contentWidth,
  contentPadding: themeConfig.config.contentPadding,
  toastPosition: themeConfig.config.toastPosition,
  verticalNavToggleType: themeConfig.config.verticalNavToggleType,
  appBar:
    themeConfig.config.layout === 'horizontal' && themeConfig.config.appBar === 'hidden'
      ? 'fixed'
      : themeConfig.config.appBar,
});

const getStaticSettings = () => {
  const initialSettings = getInitialSettings();
  return {
    appBar: initialSettings.appBar,
    backTheme: initialSettings.backTheme,
    backgroundFilter: initialSettings.backgroundFilter,
    footer: initialSettings.footer,
    layout: initialSettings.layout,
    navHidden: initialSettings.navHidden,
    navBelow: initialSettings.navBelow,
    lastLayout: initialSettings.lastLayout,
    menuTextHidden: initialSettings.menuTextHidden,
    toastPosition: initialSettings.toastPosition,
  };
};

const restoreSettings = (): Settings | null => {
  let settings: Settings | null = null;

  try {
    const storedData: string | null = readUserScopedItem('settings');

    if (storedData) {
      settings = { ...getInitialSettings(), ...JSON.parse(storedData), ...getStaticSettings() };
    } else {
      settings = getInitialSettings();
    }
  } catch (err: unknown) {
    exception(err, 'Failed to parse stored settings from localStorage');
  }

  return settings;
};

// set settings in localStorage
const storeSettings = (settings: Settings) => {
  const initSettings = Object.assign({}, settings);

  delete initSettings.appBar;
  delete initSettings.backTheme;
  delete initSettings.backgroundFilter;
  delete initSettings.footer;
  delete initSettings.layout;
  delete initSettings.navHidden;
  delete initSettings.navBelow;
  delete initSettings.lastLayout;
  delete initSettings.toastPosition;
  userScopedStorage.setItem('settings', JSON.stringify(initSettings));
};

/**
 * Resolves the active palette mode from user preference and system color scheme.
 */
const resolveModeFromPreference = (
  settings: Settings,
  systemColorScheme: PaletteMode,
): PaletteMode => {
  if (settings.preferredMode === 'system') {
    return systemColorScheme;
  }
  if (settings.preferredMode === 'semi-dark') {
    return 'light';
  }
  return settings.preferredMode;
};

// ** Create Context
export const SettingsContext = createContext<SettingsContextValue>({
  saveSettings: () => null,
  settings: getInitialSettings(),
});

const SettingsContextProviderRoot = SettingsContext.Provider as React.ComponentType<
  React.ProviderProps<SettingsContextValue> & { className?: string }
>;

export const SettingsProvider = ({ children, pageSettings, className }: SettingsProviderProps) => {
  'use memo';

  // ** State
  const [settings, setSettings] = useState<Settings>({ ...getInitialSettings() });
  const [systemColorScheme, setSystemColorScheme] = useState<PaletteMode>('light');
  const storageUserId = useStorageUserId();

  useSystemColorScheme({ setMode: setSystemColorScheme });

  useLayoutEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings(restoredSettings);
    }
  }, [storageUserId]);

  useEffect(() => {
    if (!pageSettings) {
      return;
    }

    setSettings((current) => {
      const hasChange = (Object.keys(pageSettings) as Array<keyof Settings>).some(
        (key) => current[key] !== pageSettings[key],
      );
      if (!hasChange) {
        return current;
      }

      const updated = { ...current, ...pageSettings };
      storeSettings(updated);
      return updated;
    });
  }, [pageSettings]);

  useEffect(() => {
    setSettings((current) => {
      let updated = current;

      if (current.layout === 'horizontal' && current.preferredMode === 'semi-dark') {
        updated = { ...updated, mode: 'light' };
      }
      if (current.layout === 'horizontal' && current.appBar === 'hidden') {
        updated = { ...updated, appBar: 'fixed' };
      }

      if (updated.mode === current.mode && updated.appBar === current.appBar) {
        return current;
      }

      storeSettings(updated);
      return updated;
    });
  }, [settings.layout]);

  useEffect(() => {
    setSettings((current) => {
      const nextMode = resolveModeFromPreference(current, systemColorScheme);

      if (current.mode === nextMode) {
        return current;
      }

      const updated = { ...current, mode: nextMode };
      storeSettings(updated);
      return updated;
    });
  }, [systemColorScheme, settings.preferredMode]);

  // keep: written into context value consumed via SettingsContext; unstable identity would re-render all consumers
  const saveSettings = useCallback((updatedSettings: SettingsUpdater) => {
    if (typeof updatedSettings === 'function') {
      setSettings((current) => {
        const next = updatedSettings(current);
        if (next === current) {
          return current;
        }
        storeSettings(next);
        return next;
      });
      return;
    }

    storeSettings(updatedSettings);
    setSettings(updatedSettings);
  }, []);

  // keep: value consumed via SettingsContext by many subscribers; unstable identity would re-render all of them
  const contextValue = useMemo(
    (): SettingsContextValue => ({ settings, saveSettings }),
    [settings, saveSettings],
  );

  return (
    <SettingsContextProviderRoot
      className={clsx('settings-provider', className)}
      value={contextValue}
    >
      {children}
    </SettingsContextProviderRoot>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;
